import { useRef } from "react";
import { Props as SelectProps } from "react-select";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getSpecialties } from "@/services/sintegrada/specialty/getSpecialties";
import { filterOptions } from "@/utils/filterOptions";
import { Option } from "@/@types/generics/Option";
import { MedicalTypeEnum } from "@/utils/enums/MedicalTypeEnum";

import { SelectComponent } from "../SelectComponent";

type Props = SelectProps & {
	noDefaultOption?: boolean;
};

export type MedicalTypeValue = {
	medicalType: MedicalTypeEnum;
	specialtyId: number | null;
};

export type MedicalTypeOption = Option<MedicalTypeValue | null>;

export const defaultMedicalTypeOption: MedicalTypeOption = {
	label: "Todos",
	value: null,
};

export function MedicalTypeSelect({ noDefaultOption, ...rest }: Props) {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const loadedOptions = useRef<MedicalTypeOption[]>([]);
	
	async function loadOptions(inputValue: string) {
		const hasLoadedOptions = Boolean(loadedOptions.current.length);

		if (hasLoadedOptions) {
			return filterOptions(inputValue, loadedOptions.current);
		}

		const specialties = await getSpecialties({
			uf,
			ibgeCode,
			cnes,
		});

		const consultationOption = {
			label: "Consultório Médico",
			value: {
				medicalType: MedicalTypeEnum.CONSULTORIO_MEDICO,
				specialtyId: null,
			},
		};

		const specialtyOptions = specialties.map((specialty) => ({
			label: `Especialidade - ${specialty.name}`,
			value: {
				medicalType: MedicalTypeEnum.CONSULTORIO_ESPECIALIDADE,
				specialtyId: specialty.id,
			},
		}));

		const options = [
			defaultMedicalTypeOption,
			consultationOption,
			...specialtyOptions,
		];

		loadedOptions.current = options;

		return options;
	}

	return (
		<div className="d-flex flex-column">
			<label title="Tipo de consultório médico" htmlFor={rest.id} className="mb-2">
				Tipo de Consultório:
			</label>

			<SelectComponent
				isAsync
				defaultOptions
				id={rest.id}
				className="fs-8 p-0"
				loadOptions={loadOptions}
				placeholder="Selecione..."
				{...rest}
			/>
		</div>
	);
}
